import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import GlobalContext from "../context/GlobalContext";
import useLogin from "../services/useLogin";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown } from "lucide-react";

function NavbarWeb() {
  const { userInfo } = useContext(GlobalContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { Logout } = useLogin();

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <header className="w-full bg-green-50 backdrop-blur-sm sticky top-0 z-50">
      <div className="px-4 sm:px-6 md:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between py-4">
          <Link
            to="/"
            className="flex items-center gap-3 font-bold text-2xl text-slate-800 hover:text-green-700 transition-colors"
          >
            <div className="bg-green-100 p-2 rounded-xl">
              <img
                src="/assets/logo/logo.svg"
                alt="MedTag Logo"
                className="h-6 w-6"
              />
            </div>
            MedTag
          </Link>

          <div className="hidden md:flex flex-1 justify-center">
            <nav className="flex items-center gap-6 text-sm">
              <Link
                to="/"
                onClick={closeSidebar}
                className="hover:text-green-700"
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-slate-600 hover:text-green-700 transition-colors font-medium"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="text-slate-600 hover:text-green-700 transition-colors font-medium"
              >
                Contact
              </Link>
              <Link
                to="/membership"
                className="text-slate-600 hover:text-green-700 transition-colors font-medium"
              >
                Pricing Plans
              </Link>
            </nav>
          </div>

          <div className="hidden md:flex items-center gap-3 text-sm">
            {!userInfo ? (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:border-green-500 hover:text-green-700 transition-all font-medium"
                >
                  Login
                </Link>
                <Link
                  to="/sign-up"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md"
                >
                  Sign Up
                </Link>
              </>
            ) : (
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton className="flex items-center gap-2 px-2 py-1 rounded-md">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-700 font-semibold text-sm">
                      {userInfo.first_name?.charAt(0)}
                    </span>
                  </div>
                  <span className="font-semibold text-slate-700 hidden sm:block">
                    {userInfo.first_name}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </MenuButton>

                <MenuItems className="absolute right-0 mt-2 w-40 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg focus:outline-none z-50">
                  <div className="py-1">
                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/edit-profile"
                          className={`block px-4 py-2 text-sm ${
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          Edit Profile
                        </Link>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={Logout}
                          className={`${
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700"
                          } block w-full px-4 py-2 text-left text-sm`}
                        >
                          Sign out
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>
            )}
          </div>

          <button
            className="md:hidden text-slate-800"
            onClick={() => setSidebarOpen(true)}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-0 z-40 ${sidebarOpen ? "block" : "hidden"}`}>
        <div
          className="absolute inset-0 bg-black bg-opacity-40"
          onClick={closeSidebar}
        ></div>
        <div
          className={`fixed top-0 left-0 w-1/2 h-screen bg-white shadow-2xl transform transition-transform duration-300 ease-in-out border-r border-slate-200 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <img
                  src="/assets/logo/logo.svg"
                  alt="Logo"
                  className="w-6 h-6"
                />
                <span className="text-lg font-semibold text-slate-800">
                  MedTag
                </span>
              </div>
              <button
                className="text-2xl text-slate-700"
                onClick={closeSidebar}
              >
                &times;
              </button>
            </div>

            <nav className="flex-1 px-5 py-6 flex flex-col gap-5 text-slate-700 font-medium">
              <Link
                to="/"
                onClick={closeSidebar}
                className="hover:text-green-700"
              >
                Home
              </Link>
              <Link
                to="/about"
                onClick={closeSidebar}
                className="hover:text-green-700"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={closeSidebar}
                className="hover:text-green-700"
              >
                Contact
              </Link>
              <Link
                to="/membership"
                className="text-slate-600 hover:text-green-700 transition-colors font-medium"
              >
                Pricing Plans
              </Link>
            </nav>

            <div className="px-5 py-6 mt-auto flex flex-col gap-4">
              {!userInfo ? (
                <div className="flex flex-col gap-4">
                  <Link
                    to="/login"
                    onClick={closeSidebar}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/sign-up"
                    onClick={closeSidebar}
                    className="bg-white hover:bg-gray-50 border-2 border-green-600 text-green-600 hover:text-green-700 px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-center"
                  >
                    Sign Up
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => {
                    closeSidebar();
                  }}
                  className="text-red-600 hover:underline text-left"
                >
                  Sign Out
                </button>
              )}

              <div className="text-xs text-slate-400 mt-6 border-t pt-4">
                © 2025 MedTag
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default NavbarWeb;
